/**
 * Mention syntax shared with the server (services/mentions.ts):
 *   @[Display Name](member:<userId>)   @[Display Name](guest:<guestId>)
 * Pure helpers for formatting, parsing, rendering and the composer's autocomplete.
 */
export type MentionKind = 'member' | 'guest';

export interface MentionRef {
  kind: MentionKind;
  id: string;
  name: string;
}

export interface MentionCandidate extends MentionRef {
  /** Secondary text in the autocomplete list (e-mail, "guest", link name). */
  detail?: string;
}

export const MENTION_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
export const MAX_MENTION_NAME = 100;
const MENTION_SRC = '@\\[([^\\]\\n]{1,100})\\]\\((member|guest):([A-Za-z0-9_-]{1,64})\\)';

/** Names may not contain brackets or line breaks (they would end the mention token). */
export function sanitizeMentionName(name: string): string {
  const clean = name
    .replace(/[[\]\r\n]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_MENTION_NAME)
    .trim();
  return clean === '' ? '?' : clean;
}

export function mentionKey(m: { kind: MentionKind; id: string }): string {
  return `${m.kind}:${m.id}`;
}

export function formatMention(m: MentionRef): string {
  if (!MENTION_ID_RE.test(m.id)) throw new RangeError(`Invalid mention id "${m.id}"`);
  return `@[${sanitizeMentionName(m.name)}](${m.kind}:${m.id})`;
}

/** All mentions in order of appearance, de-duplicated by kind + id (first name wins). */
export function parseMentions(body: string): MentionRef[] {
  const seen = new Set<string>();
  const out: MentionRef[] = [];
  for (const m of body.matchAll(new RegExp(MENTION_SRC, 'g'))) {
    const ref: MentionRef = { kind: m[2] as MentionKind, id: m[3]!, name: m[1]! };
    const key = mentionKey(ref);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ref);
  }
  return out;
}

export type BodySegment =
  { type: 'text'; text: string } | ({ type: 'mention'; raw: string } & MentionRef);

/** Splits a comment body into text and mention segments (for rendering mention chips). */
export function tokenizeBody(body: string): BodySegment[] {
  const out: BodySegment[] = [];
  let last = 0;
  for (const m of body.matchAll(new RegExp(MENTION_SRC, 'g'))) {
    const at = m.index;
    if (at > last) out.push({ type: 'text', text: body.slice(last, at) });
    out.push({ type: 'mention', raw: m[0], kind: m[2] as MentionKind, id: m[3]!, name: m[1]! });
    last = at + m[0].length;
  }
  if (last < body.length) out.push({ type: 'text', text: body.slice(last) });
  return out;
}

/** Body with mentions shown as "@Name" (excerpts, notifications, screen readers). */
export function plainText(body: string): string {
  return tokenizeBody(body)
    .map((s) => (s.type === 'text' ? s.text : `@${s.name}`))
    .join('');
}

export interface MentionQuery {
  /** Index of the "@" that starts the query. */
  start: number;
  query: string;
}

export const MAX_QUERY = 40;

/**
 * The mention being typed at the caret: an "@" at the start or after whitespace / "(", followed
 * by up to 40 characters without line breaks, "@" or brackets.
 */
export function activeMentionQuery(text: string, caret: number): MentionQuery | null {
  const upto = text.slice(0, Math.max(0, Math.min(caret, text.length)));
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  const query = upto.slice(at + 1);
  if (query.length > MAX_QUERY || /[\n@[\]()]/.test(query)) return null;
  const before = at === 0 ? '' : upto[at - 1]!;
  if (before !== '' && !/[\s(]/.test(before)) return null;
  return { start: at, query };
}

/** Replaces the query (from "@" up to the caret) with the formatted mention plus a space. */
export function applyMention(
  text: string,
  caret: number,
  q: MentionQuery,
  m: MentionRef,
): { text: string; caret: number } {
  const token = `${formatMention(m)} `;
  const end = Math.max(q.start, Math.min(caret, text.length));
  return { text: text.slice(0, q.start) + token + text.slice(end), caret: q.start + token.length };
}

/**
 * The composer shows mentions as "@Name" (no ids in the text field, OIL-179): the body's
 * tokens become plain names, the refs are kept beside the text.
 */
export function toDisplay(body: string): { text: string; refs: MentionRef[] } {
  return { text: plainText(body), refs: parseMentions(body) };
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Back to the server syntax: every "@Name" of a kept ref (longest names first, followed by a
 * non-word character or the end) becomes its token; a name the user edited stays plain text.
 */
export function toBody(text: string, refs: readonly MentionRef[]): string {
  const byName = new Map<string, MentionRef>();
  for (const r of refs)
    if (!byName.has(sanitizeMentionName(r.name))) byName.set(sanitizeMentionName(r.name), r);
  const names = [...byName.keys()].sort((a, b) => b.length - a.length);
  if (names.length === 0) return text;
  const re = new RegExp(`@(${names.map(escapeRe).join('|')})(?![\\p{L}\\p{N}_])`, 'gu');
  return text.replace(re, (_m, name: string) => formatMention(byName.get(name)!));
}

/** Like applyMention, but inserts the display form "@Name " (see toDisplay / toBody). */
export function applyMentionDisplay(
  text: string,
  caret: number,
  q: MentionQuery,
  m: MentionRef,
): { text: string; caret: number } {
  const token = `@${sanitizeMentionName(m.name)} `;
  const end = Math.max(q.start, Math.min(caret, text.length));
  return { text: text.slice(0, q.start) + token + text.slice(end), caret: q.start + token.length };
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Case/diacritics-insensitive: name prefix matches first, then word prefixes, then substrings. */
export function filterCandidates<C extends MentionCandidate>(
  cands: readonly C[],
  query: string,
  limit = 8,
): C[] {
  const q = fold(query.trim());
  const scored: { c: C; score: number }[] = [];
  for (const c of cands) {
    const name = fold(c.name);
    const detail = fold(c.detail ?? '');
    let score: number;
    if (q === '' || name.startsWith(q)) score = 0;
    else if (name.split(/\s+/).some((w) => w.startsWith(q))) score = 1;
    else if (name.includes(q) || detail.includes(q)) score = 2;
    else continue;
    scored.push({ c, score });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.c.name.localeCompare(b.c.name))
    .slice(0, limit)
    .map((s) => s.c);
}

export interface CandidateSources {
  /** The person writing (never suggested). */
  self: { kind: MentionKind; id: string };
  /** Workspace members (members only; guests cannot list them). */
  members: readonly { userId: string; name: string; email?: string }[];
  /** Visible comments: their authors are suggested (members, and guests of the same link). */
  authors: readonly {
    kind: MentionKind;
    id: string;
    name: string;
    viaLink: string | null;
    linkId: string | null;
  }[];
  /**
   * Link scope of the comment being written (a guest's own link, or the link of the thread a
   * member replies to). Guests are only valid mentions within that link (server rule).
   */
  linkId: string | null;
  /** Server list for guests (GET /share/:token/mentionable): members + guests of their link. */
  mentionable?: readonly { kind: MentionKind; id: string; name: string }[];
}

/** Who may be mentioned: workspace members, plus guests of the comment's link. De-duplicated. */
export function mentionCandidates(src: CandidateSources): MentionCandidate[] {
  const out = new Map<string, MentionCandidate>();
  const add = (c: MentionCandidate) => {
    const key = mentionKey(c);
    if (key === mentionKey(src.self) || out.has(key) || !MENTION_ID_RE.test(c.id)) return;
    out.set(key, c);
  };
  for (const m of src.mentionable ?? []) add({ kind: m.kind, id: m.id, name: m.name });
  for (const m of src.members)
    add({ kind: 'member', id: m.userId, name: m.name, ...(m.email ? { detail: m.email } : {}) });
  for (const a of src.authors) {
    if (a.kind === 'member') add({ kind: 'member', id: a.id, name: a.name });
    else if (src.linkId !== null && a.linkId === src.linkId) {
      add({ kind: 'guest', id: a.id, name: a.name, ...(a.viaLink ? { detail: a.viaLink } : {}) });
    }
  }
  return [...out.values()];
}
